import React, { useState } from 'react';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import { CircularProgress, Fab, List } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useQuery } from '@apollo/client';

import Copyright from '../../components/common/Copyright';
import ErrorView from '../../components/common/ErrorView';
import EditPlatformClient from './EditPlatformClient';
import PlatformClientRow, { Tenant_RowFragment } from './PlatformClientRow';
import useGmdUserInfo from '../../hooks/gmdUserInfo';

import { graphql, FragmentType } from '../../__generated__';
import { pollServer } from '../../graphql/utils';

export const getTenantsQuery = graphql(/* GraphQL */ `
  query GetTenants {
    tenants {
      ...Tenant_RowFragment
    }
  }
`);

const PlatformClientViewContainer = () => {
  const { canAddClient, canEditClient } = useGmdUserInfo();
  const { data, loading, error, startPolling, stopPolling } =
    useQuery(getTenantsQuery);
  const [openAdd, setOpenAdd] = useState<boolean>(false);
  const [openEdit, setOpenEdit] = useState<boolean>(false);
  const [selectedTenant, setSelectedTenant] =
    useState<FragmentType<typeof Tenant_RowFragment>>();

  const onOpenEdit = () => {
    pollServer(startPolling, stopPolling);
    setOpenEdit(true);
  };

  const onCloseEdit = () => {
    setOpenEdit(false);
    pollServer(startPolling, stopPolling);
  };

  const onOpenAdd = () => {
    stopPolling();
    setOpenAdd(true);
  };

  const onCloseAdd = () => {
    setOpenAdd(false);
    pollServer(startPolling, stopPolling);
  };

  if (error) {
    return <ErrorView message={error?.message || ''} />;
  }

  const tenants = data?.tenants || [];

  return (
    <Container component="main">
      <CssBaseline />
      <Box
        sx={{
          marginTop: 2,
          marginBottom: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        {(!tenants || tenants.length === 0) && <div>No available clients</div>}
        {tenants && tenants.length > 0 && (
          <List
            sx={{ width: '100%', maxWidth: 800, bgcolor: 'background.paper' }}
            data-testid={'platform-client-list'}
          >
            {tenants.map((tenant, index) => {
              if (tenant === null) return null;
              return (
                <PlatformClientRow
                  key={index}
                  tenant={tenant}
                  onEdit={onOpenEdit}
                  canEdit={canEditClient}
                  setSelectedTenant={setSelectedTenant}
                />
              );
            })}
          </List>
        )}
        {canAddClient && (
          <Fab
            sx={{ position: 'fixed', bottom: 50, right: 50 }}
            aria-label={'Add'}
            color={'primary'}
            disabled={loading}
            onClick={onOpenAdd}
          >
            <AddIcon />
          </Fab>
        )}
        {loading && <CircularProgress />}
      </Box>
      {selectedTenant && (
        <EditPlatformClient
          onClose={onCloseEdit}
          open={openEdit}
          tenant={selectedTenant}
        />
      )}
      <EditPlatformClient open={openAdd} onClose={onCloseAdd} />
      <Copyright />
    </Container>
  );
};

export default PlatformClientViewContainer;
